import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  numberAttribute,
  Output,
} from '@angular/core';

function numberOrZero(value: unknown): number {
  return numberAttribute(value, 0);
}

function numberOrOneHundred(value: unknown): number {
  return numberAttribute(value, 100);
}

@Component({
  selector: 'ds-slider',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-slider-wrap"
      [class.ds-slider-wrap--disabled]="disabled"
      [style.--ds-slider-progress]="progressPercent + '%'"
      [style.--ds-slider-progress-ratio]="progressRatioCss"
      [style.--ds-slider-value-left]="valueLabelLeft"
    >
      @if (showValueLabel) {
        <output class="ds-slider-value ds-text--visual-padding-none" aria-hidden="true">{{ displayValueLabel }}</output>
      }
      <div class="ds-slider-visual" aria-hidden="true">
        <div class="ds-slider-track">
          <div class="ds-slider-track__active"></div>
          @if (showTickMarks && tickFractions.length) {
            <div class="ds-slider-ticks">
              @for (tick of tickFractions; track tick.fraction) {
                <span
                  class="ds-slider-tick"
                  [class.ds-slider-tick--active]="tick.fraction <= progressRatio"
                  [style.left]="tickPosition(tick.fraction)"
                ></span>
              }
            </div>
          }
        </div>
        <span class="ds-slider-thumb-visual"></span>
      </div>
      <input
        class="ds-slider"
        type="range"
        [min]="min"
        [max]="max"
        [attr.step]="nativeStep"
        [value]="value"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel"
        [attr.aria-valuemin]="min"
        [attr.aria-valuemax]="max"
        [attr.aria-valuenow]="value"
        (input)="onInput($event)"
        (change)="changed.emit(value)"
        (pointerdown)="onPointerDown($event)"
        (dragstart)="onNativeDragStart($event)"
        (focus)="focused.emit($event)"
        (blur)="blurred.emit($event)"
      />
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: var(--ds-slider-min-width);
    }

    .ds-slider-wrap {
      --ds-slider-progress: 0%;
      --ds-slider-value-left: var(--ds-slider-thumb-size);
      --ds-slider-value-size: calc(var(--ds-slider-thumb-size) + (var(--ds-slider-track-height) * 2));
      --ds-slider-value-tail-size: calc(var(--ds-slider-track-height) * 2);
      --ds-slider-track-inset-local: calc(var(--ds-slider-thumb-size) / 2);
      --ds-slider-tick-inset-local: calc(var(--ds-slider-track-inset, calc(var(--ds-slider-track-height) + var(--ds-slider-thumb-border-width))) / 2);
      --ds-slider-tick-size-local: var(--ds-slider-tick-size, 4px);
      position: relative;
      min-width: var(--ds-slider-min-width);
      padding-top: calc(var(--ds-slider-value-size) + var(--ds-slider-value-tail-size));
    }

    .ds-slider-wrap--disabled {
      opacity: 0.44;
    }

    .ds-slider-wrap:has(.ds-slider:focus-visible)::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: calc((var(--ds-focus-gap) + var(--ds-focus-stroke)) * -1);
      height: calc(var(--ds-slider-thumb-size) + (var(--ds-focus-gap) * 3) + var(--ds-focus-stroke));
      border: var(--ds-focus-stroke) solid var(--ds-focus-color);
      border-radius: var(--ds-slider-focus-border-radius);
      pointer-events: none;
    }

    .ds-slider-value {
      position: absolute;
      left: var(--ds-slider-value-left);
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: var(--ds-slider-value-size);
      height: var(--ds-slider-value-size);
      padding: 0 calc(var(--ds-slider-track-height) * 2);
      transform: translateX(-50%);
      border-radius: var(--ds-slider-border-radius);
      background: var(--ds-slider-thumb-fill-color);
      color: var(--ds-slider-thumb-surface-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-slider-thumb-inner-size);
      font-weight: 800;
      line-height: 1;
      pointer-events: none;
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }

    .ds-slider-value::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: calc(var(--ds-slider-value-tail-size) * -1);
      width: 0;
      height: 0;
      transform: translateX(-50%);
      border-left: calc(var(--ds-slider-value-tail-size) / 2) solid transparent;
      border-right: calc(var(--ds-slider-value-tail-size) / 2) solid transparent;
      border-top: var(--ds-slider-value-tail-size) solid var(--ds-slider-thumb-fill-color);
    }

    .ds-slider-visual {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: var(--ds-slider-thumb-size);
      pointer-events: none;
      z-index: 0;
    }

    .ds-slider-track {
      position: absolute;
      left: var(--ds-slider-track-inset-local);
      right: var(--ds-slider-track-inset-local);
      top: 50%;
      height: var(--ds-slider-track-height);
      transform: translateY(-50%);
      border-radius: var(--ds-slider-border-radius);
      background: var(--ds-slider-track-inactive-color);
    }

    .ds-slider-thumb-visual {
      position: absolute;
      left: var(--ds-slider-value-left);
      top: 50%;
      width: var(--ds-slider-thumb-size);
      height: var(--ds-slider-thumb-size);
      transform: translate(-50%, -50%);
      border: var(--ds-slider-thumb-border-width) solid var(--ds-slider-thumb-border-color);
      border-radius: 50%;
      background:
        radial-gradient(
          circle,
          var(--ds-slider-thumb-fill-color) 0,
          var(--ds-slider-thumb-fill-color) calc(var(--ds-slider-thumb-inner-size) / 2),
          var(--ds-slider-thumb-surface-color) calc(var(--ds-slider-thumb-inner-size) / 2 + 1px),
          var(--ds-slider-thumb-surface-color) 100%
        );
      box-sizing: border-box;
      z-index: 3;
    }

    .ds-slider-track__active {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: var(--ds-slider-progress);
      border-radius: var(--ds-slider-border-radius);
      background: var(--ds-slider-track-active-color);
      overflow: hidden;
    }

    .ds-slider-ticks {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 1;
    }

    .ds-slider-tick {
      position: absolute;
      top: 50%;
      width: var(--ds-slider-tick-size-local);
      height: var(--ds-slider-tick-size-local);
      transform: translate(-50%, -50%);
      border-radius: var(--ds-slider-border-radius);
      background: var(--ds-slider-track-active-color);
    }

    .ds-slider-tick--active {
      background: var(--ds-slider-thumb-surface-color);
    }

    .ds-slider {
      position: relative;
      z-index: 2;
      width: 100%;
      height: var(--ds-slider-thumb-size);
      margin: 0;
      appearance: none;
      cursor: pointer;
      outline: none;
      background: transparent;
      border-radius: var(--ds-slider-border-radius);
      touch-action: none;
      user-select: none;
      -webkit-user-drag: none;
    }

    .ds-slider:disabled {
      cursor: not-allowed;
    }

    .ds-slider:focus-visible {
      outline: none;
    }

    .ds-slider::-webkit-slider-runnable-track {
      height: var(--ds-slider-track-height);
      border-radius: var(--ds-slider-border-radius);
      background: transparent;
    }

    .ds-slider::-webkit-slider-thumb {
      appearance: none;
      width: var(--ds-slider-thumb-size);
      height: var(--ds-slider-thumb-size);
      margin-top: calc((var(--ds-slider-track-height) - var(--ds-slider-thumb-size)) / 2);
      border: none;
      border-radius: 50%;
      background: transparent;
      box-sizing: border-box;
    }

    .ds-slider::-moz-range-track {
      height: var(--ds-slider-track-height);
      border-radius: var(--ds-slider-border-radius);
      background: transparent;
    }

    .ds-slider::-moz-range-progress {
      height: var(--ds-slider-track-height);
      border-radius: var(--ds-slider-border-radius);
      background: transparent;
    }

    .ds-slider::-moz-range-thumb {
      width: var(--ds-slider-thumb-size);
      height: var(--ds-slider-thumb-size);
      border: none;
      border-radius: 50%;
      background: transparent;
      box-sizing: border-box;
    }
  `],
})
export class DsSliderComponent {
  @Input({ transform: numberOrZero }) value = 0;
  @Input({ transform: numberOrZero }) min = 0;
  @Input({ transform: numberOrOneHundred }) max = 100;
  @Input({ transform: numberOrZero }) step = 0;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() ariaLabel = 'Slider';
  @Input({ transform: booleanAttribute }) showValueLabel = true;
  @Input() valueLabel: string | null = null;
  @Input({ transform: booleanAttribute }) showTickMarks = false;

  @Output() valueChange = new EventEmitter<number>();
  @Output() changed = new EventEmitter<number>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  get progressPercent(): number {
    const span = this.max - this.min;
    if (span <= 0) return 0;
    return Math.min(100, Math.max(0, ((this.value - this.min) / span) * 100));
  }

  get progressRatio(): number {
    return this.progressPercent / 100;
  }

  get valueLabelLeft(): string {
    return `calc((var(--ds-slider-thumb-size) / 2) + (100% - var(--ds-slider-thumb-size)) * ${this.progressPercent / 100})`;
  }

  get progressRatioCss(): string {
    return String(this.progressRatio);
  }

  get displayValueLabel(): string {
    return this.valueLabel ?? String(Math.round(this.value));
  }

  get nativeStep(): number | 'any' {
    return Number(this.step) > 0 ? this.step : 'any';
  }

  get tickFractions(): Array<{ fraction: number }> {
    const step = Number(this.step);
    const span = this.max - this.min;
    if (step <= 0 || span <= 0) return [];
    const intervalCount = Math.floor(span / step);
    if (intervalCount < 1) return [];
    return Array.from({ length: intervalCount + 1 }, (_, index) => ({
      fraction: index / intervalCount,
    }));
  }

  tickPosition(fraction: number): string {
    return `calc(var(--ds-slider-tick-inset-local) + (100% - (var(--ds-slider-tick-inset-local) * 2)) * ${fraction})`;
  }

  onInput(event: Event): void {
    const next = Number((event.target as HTMLInputElement).value);
    this.value = next;
    this.valueChange.emit(next);
  }

  onPointerDown(event: PointerEvent): void {
    if (this.disabled) return;

    const input = event.currentTarget as HTMLInputElement;
    if (!input.setPointerCapture) return;

    try {
      input.setPointerCapture(event.pointerId);
    } catch {
      // Browsers can reject capture if native range handling already released the pointer.
    }
  }

  onNativeDragStart(event: DragEvent): void {
    event.preventDefault();
  }
}
